import { useState, useEffect } from 'react';
import { Modal, Spinner, Alert } from '@components/base/feedback';
import type {
  VeosClassementOption,
  VeosDocumentRow,
  VeosMailAttachment,
} from '../../api/documentsService';
import { EmailViewConfigurable } from './EmailView';
import { parseReceivedEmail, type ParsedReceivedEmail } from './emailParser';
import { formatEmailDate, resolveContentType } from './documentHelpers';

interface EmailAttachmentModalProps {
  open: boolean;
  onClose: () => void;
  /** An email-typed attachment (`.msg` / `.eml`) of the email being viewed. */
  attachment: VeosMailAttachment;
  classementOptions: VeosClassementOption[];
  /** Documents list — drives the nested rows' "already in GED" state. */
  documents: VeosDocumentRow[];
  /** id_sin — the GED entity id for the nested rows' `document-create`. */
  idSin: string;
  /** Resolves the attachment's base64 content (same resolver as its row). */
  resolveContent: (attachment: VeosMailAttachment) => Promise<string>;
  /** Bubbled up from the nested rows so the host refreshes its documents. */
  onGedded: () => void;
}

type AttachedEmailContentProps = Omit<EmailAttachmentModalProps, 'open' | 'onClose'>;

const LOAD_ERROR = "Erreur lors de l'ouverture de l'e-mail joint.";

/**
 * The nested email's own attachments were decoded by the parser into inline
 * base64 `data` — no round-trip needed. Module-level so the nested rows get a
 * stable resolver (their size / open effects depend on it).
 */
function resolveInlineAttachment(attachment: VeosMailAttachment): Promise<string> {
  return attachment.data
    ? Promise.resolve(attachment.data)
    : Promise.reject(new Error('Attachment content is not available inline.'));
}

/**
 * Resolve + parse the attached email, then render it with the registered
 * `EmailView`. Mounted only while the modal is open (the `Modal` renders no
 * children when closed), so nothing is parsed until the user asks for it.
 */
function AttachedEmailContent({
  attachment,
  classementOptions,
  documents,
  idSin,
  resolveContent,
  onGedded,
}: Readonly<AttachedEmailContentProps>) {
  const [parsed, setParsed] = useState<ParsedReceivedEmail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const base64 = await resolveContent(attachment);
      // Same best-effort parse as `EmailDetail`: an unparseable message still
      // yields a view (notice + the original file as single attachment).
      const result = await parseReceivedEmail(
        base64,
        resolveContentType(attachment.name, attachment.contentType),
        attachment.name,
        attachment.name,
      );
      if (result.email.date) {
        result.email.date = formatEmailDate(result.email.date);
      }
      if (!cancelled) setParsed(result);
    };

    void load().catch(() => {
      if (!cancelled) setLoadError(LOAD_ERROR);
    });

    return () => {
      cancelled = true;
    };
  }, [attachment, resolveContent]);

  if (loadError) return <Alert variant="error">{loadError}</Alert>;

  if (!parsed) {
    return (
      <div className="flex items-center justify-center min-h-48">
        <Spinner size="md" />
      </div>
    );
  }

  // The registered, rule-aware view — the same one `EmailDetail` renders. Its
  // own email-typed attachments open one level deeper: each
  // `EmailAttachmentRow` owns its own modal.
  return (
    <EmailViewConfigurable
      email={parsed.email}
      classementOptions={classementOptions}
      documents={documents}
      idSin={idSin}
      resolveAttachment={resolveInlineAttachment}
      onGedded={onGedded}
      notice={parsed.notice}
    />
  );
}

/**
 * Opens an email attached to an email (an Outlook message embedded in a `.msg`,
 * or a `message/rfc822` part of an EML) in-app (mahhp 22844). A browser cannot
 * render such a file, so opening it in a tab only downloaded it.
 *
 * Pure chrome around the registered `claimflow.emailView`: every user-facing
 * label inside comes from `EmailView`, so this modal is not registered itself.
 *
 * Note the deliberate, render-time-only import cycle `EmailView` →
 * `EmailAttachmentRow` → `EmailAttachmentModal` → `EmailView` (that is what
 * makes the recursion work): `EmailViewConfigurable` is only read while
 * rendering, never at module evaluation, so the cycle resolves safely.
 */
export function EmailAttachmentModal({
  open,
  onClose,
  attachment,
  ...contentProps
}: Readonly<EmailAttachmentModalProps>) {
  return (
    <Modal open={open} onClose={onClose} size="lg">
      <Modal.Header title={attachment.name} />
      <Modal.Body>
        <AttachedEmailContent attachment={attachment} {...contentProps} />
      </Modal.Body>
    </Modal>
  );
}
